import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

// Word count helper (trim, split by whitespace)
const wordCount = (val: string) => (val || "").trim().split(/\s+/).filter(w => w.length > 0).length;

const formSchema = z.object({
  title: z
    .string()
    .min(1, "Title is required")
    .refine((v) => wordCount(v) <= 15, { message: "Title max 15 words" }),
  description: z
    .string()
    .min(1, "Description is required")
    .refine((v) => wordCount(v) <= 30, { message: "Description max 30 words" }),
  type: z.enum(["gut", "nutrition", "sleep"], {
    required_error: "Select a type",
  }),
  duration: z.coerce.number().min(0, "Duration cannot be negative"),
  duration_type: z.enum(["minute", "hour", "day"], {
    required_error: "Select a duration type",
  }),
  reward_time: z.coerce.number().min(0, "Reward time cannot be negative"),
  reward_time_type: z.enum(["minute", "hour", "day"], {
    required_error: "Select a reward time type",
  }),
});

export type ChallengeEditForm = z.infer<typeof formSchema>;

interface ChallengeEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form: ChallengeEditForm;
  setForm: React.Dispatch<React.SetStateAction<ChallengeEditForm>>;
  onSave: (data: ChallengeEditForm) => Promise<void> | void;
}

export default function ChallengeEditDialog({
  open,
  onOpenChange,
  form,
  setForm,
  onSave,
}: ChallengeEditDialogProps) {
  const [loading, setLoading] = React.useState(false);

  const rhf = useForm<ChallengeEditForm>({
    resolver: zodResolver(formSchema),
    defaultValues: form,
    values: form,
  });

  React.useEffect(() => {
    rhf.reset(form);
  }, [form, open]);

  async function handleSubmit(data: ChallengeEditForm) {
    setLoading(true);
    // Force time units to 'day' to match Add Challenge UX
    const merged: ChallengeEditForm = {
      ...form,
      ...data,
      duration_type: "day",
      reward_time_type: "day",
    };
    setForm(merged);
    await onSave(merged);
    setLoading(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!w-[95vw] sm:!w-full !max-w-[560px] sm:!max-w-[720px] !max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle>Edit Challenge</DialogTitle>
          <DialogDescription>Update challenge details and save changes. Title max 15 words, Description max 30 words.</DialogDescription>
        </DialogHeader>
        <Form {...rhf}>
          <form className="space-y-6" onSubmit={rhf.handleSubmit(handleSubmit)}>
            <FormField
              control={rhf.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Title <span className="text-xs text-muted-foreground">({wordCount(rhf.watch("title"))}/15 words)</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Drink 8 glasses of water today" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={rhf.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Description <span className="text-xs text-muted-foreground">({wordCount(rhf.watch("description"))}/30 words)</span>
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      rows={3}
                      placeholder="Stay hydrated throughout the day by drinking plenty of water to support your overall health and wellness"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={rhf.control}
              name="duration"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Challenge Duration (Days)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      min="0"
                      placeholder="e.g., 7 or 30"
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={rhf.control}
              name="reward_time"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Discount Reward Time (Days)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      min="0"
                      placeholder="e.g., 5 or 10"
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                    />
                  </FormControl>
                  <p className="text-xs text-muted-foreground">Discount time users earn for completing this challenge</p>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <Button className="w-full sm:w-auto" type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>Cancel</Button>
              <Button className="w-full sm:w-auto" type="submit" disabled={loading}>
                {loading ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
